import { useEffect } from "react";
import PensacolaHeroSection from "./components/HeroSection";
import PensacolaStickyNav from "./components/StickyNav";
import PensacolaProcessingTimeSection from "./components/ProcessingTimeSection";
import PensacolaServicesSection from "./components/ServicesSection";
import PensacolaLocalAreaSection from "./components/LocalAreaSection";
import PensacolaTestimonialsSection from "./components/TestimonialsSection";
import PensacolaAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import PensacolaComparisonSection from "./components/ComparisonSection";
import PensacolaMistakesSection from "./components/MistakesSection";
import PensacolaHowItWorksSection from "./components/HowItWorksSection";
import PensacolaFAQSection, { faqs } from "./components/FAQSection";
import PensacolaHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import PensacolaFinalCTA from "./components/FinalCTA";
import PensacolaTravelTimingSection from "./components/TravelTimingSection";
import PensacolaDestinationsSection from "./components/DestinationsSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function PensacolaPage() {
  useEffect(() => {
    document.title = "Emergency Passport Pensacola, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/pensacola/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Pensacola, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving the western Florida Panhandle and Emerald Coast.");
    setMeta("geo.placename", "Pensacola, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "30.4213;-87.2169");
    setMeta("ICBM", "30.4213, -87.2169");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "Pensacola Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Pensacola — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Pensacola",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Pensacola",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <PensacolaHeroSection />
      <PensacolaStickyNav />
      <PensacolaProcessingTimeSection />
      <PensacolaServicesSection />
      <PensacolaLocalAreaSection />
      <PensacolaTravelTimingSection />
      <PensacolaDestinationsSection />
      <PensacolaTestimonialsSection />
      <PensacolaAcceptanceFacilitiesSection />
      <PensacolaComparisonSection />
      <PensacolaMistakesSection />
      <PensacolaHowItWorksSection />
      <PensacolaFAQSection />
      <PensacolaHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/pensacola" state="FL" />
      <PensacolaFinalCTA />
    </div>
  );
}